import { apiRequest, BASE_URL } from "./api";

/**
 * Fetch the paginated list of admin products.
 *
 * @param {Object}      [params]
 * @param {number}      [params.page=1]        - Page number (min 1)
 * @param {number}      [params.limit=100]     - Items per page (max 100)
 * @param {string|null} [params.status]        - "approved" | "rejected" | "pending" | null = all
 * @param {string|null} [params.search]        - Free-text search (max 200 chars)
 * @param {string|null} [params.category]      - Category filter
 * @param {string|null} [params.market_code]   - Market code filter
 * @returns {Promise<any>} resData.data
 */
export async function fetchAdminProducts({
  page = 1,
  limit = 100,
  status = null,
  search = null,
  category = null,
  market_code = null,
} = {}) {
  const qs = new URLSearchParams();
  qs.set("page", page);
  qs.set("limit", limit);
  if (status)      qs.set("status",      status);
  if (search)      qs.set("search",      search);
  if (category)    qs.set("category",    category);
  if (market_code) qs.set("market_code", market_code);

  const result = await apiRequest(`/admin/products?${qs.toString()}`);
  return result.data;
}

export async function fetchProductReviewDetails(productId, ageGroup = null) {
  const query = ageGroup ? `?age_group=${ageGroup}` : "";
  const result = await apiRequest(`/admin/products/${productId}/review-details${query}`);
  return result.data;
}

/**
 * Fetch the list of available product categories.
 * Endpoint: GET /product-categories
 *
 * @returns {Promise<string[]>}
 */
export async function fetchProductCategories() {
  const result = await apiRequest(`/product-categories`);
  return result.data?.categories || [];
}

/**
 * Update a product's details.
 * Endpoint: PUT /admin/products/{product_id}
 *
 * @param {string} productId - The product ID
 * @param {Object} payload
 * @param {string} payload.product_name
 * @param {string} payload.brand_name
 * @param {string} [payload.category]
 * @param {string} [payload.description]
 * @param {string} [payload.image_url]
 * @param {string} [payload.ingredients]
 * @param {Object} [payload.nutrition_facts]
 * @returns {Promise<any>} resData.data
 */
export async function updateProductDetails(productId, payload) {
  const result = await apiRequest(`/admin/products/${productId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
  return result.data;
}

/**
 * Upload/Edit a single product image.
 * Endpoint: POST /admin/products/{product_id}/images
 *
 * @param {string} productId - The product ID
 * @param {File} file - The image file to upload
 * @param {string} imageType - One of 'front_image', 'barcode_image', 'ingredients_image', 'nutrition_image', 'back_image', 'allergen_image', 'other_image'
 * @returns {Promise<any>}
 */
export async function uploadProductImage(productId, file, imageType) {
  const formData = new FormData();
  formData.append("image_type", imageType);
  formData.append("file", file);

  const token = localStorage.getItem("tokens");
  const response = await fetch(`${BASE_URL}/admin/products/${productId}/images`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  const json = await response.json();
  if (!response.ok) {
    throw new Error(json.message || `Upload failed: ${response.status}`);
  }
  return json;
}
